// synced from loomweb blocks/command-block-view.tsx (de-Tailwind'd).
/**
 * Command block (Bash / Git execution). Faithful to loomweb's CommandBlockView:
 * a header (icon + "Bash Command" / "Git Command", a "Dangerous" flag, an
 * in-progress / failed state, a copy button), an optional active-text row, the
 * command line, and a collapsible result panel. Tailwind classes are replaced by
 * namespaced `replay-*` CSS; lucide icons by the local inline-SVG set.
 */
import type { JSX } from "react";
import { useMemo } from "react";
import type { CommandBlock } from "@shared/loom-message-block";
import { TerminalIcon, GitBranchIcon, LoaderIcon, AlertTriangleIcon } from "./icons";
import { CopyButton } from "./copy-button";
import { ToolResultPanel } from "./tool-result-panel";
import { CommandLine } from "./command-line";
import { isDangerousCommand } from "./dangerous-command";

export function CommandBlockView({ block }: { block: CommandBlock }): JSX.Element {
  const isInProgress = block.status === "in_progress" || block.status === "streaming";
  const isError = block.status === "error";
  const isDangerous = useMemo(() => isDangerousCommand(block.command), [block.command]);

  const Icon = block.commandType === "git" ? GitBranchIcon : TerminalIcon;
  const iconColor = isDangerous
    ? "replay-text-danger"
    : block.commandType === "git"
      ? "replay-text-git"
      : "replay-text-bash";
  const label = block.commandType === "git" ? "Git Command" : "Bash Command";

  return (
    <div className="replay-block replay-command-block">
      <div className="replay-block-head">
        <Icon className={`replay-icon ${iconColor}`} />
        <span className="replay-block-label">{label}</span>
        {isDangerous && (
          <span className="replay-block-flag replay-text-danger">
            <AlertTriangleIcon className="replay-icon-xs" />
            Dangerous
          </span>
        )}
        {isInProgress && (
          <span className="replay-block-flag replay-text-primary">
            <LoaderIcon className="replay-icon-xs replay-spin" />
          </span>
        )}
        {isError && (
          <span className="replay-block-flag replay-text-error">
            <AlertTriangleIcon className="replay-icon-xs" />
            Failed
          </span>
        )}
        <CopyButton text={block.command} className="replay-ml-auto" ariaLabel="复制命令" />
      </div>

      {isInProgress && block.activeText !== undefined && block.activeText.length > 0 && (
        <div className="replay-block-active">{block.activeText}</div>
      )}

      <div className="replay-command-line-wrap">
        <CommandLine command={block.command} />
      </div>

      {block.result?.output !== undefined && block.result.output.length > 0 && (
        <ToolResultPanel
          title="Output"
          content={block.result.output}
          copyAriaLabel="复制输出"
          defaultCollapsed={block.collapsed ?? true}
          {...(block.result.exitCode !== undefined ? { exitCode: block.result.exitCode } : {})}
          status={block.status}
        />
      )}
    </div>
  );
}
